import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import SectionHeading from '@/components/feature/SectionHeading';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useFavorites } from '@/hooks/useFavorites';
import { formatDuration, getMovieById, getRelatedMovies } from '@/utils/movies';

export default function Detail() {
  const { id } = useParams();
  const movie = getMovieById(id);
  const { toggleFavorite, isFavorite } = useFavorites();
  const [toast, setToast] = useState('');

  const favorited = movie ? isFavorite(movie.id) : false;

  usePageMeta({
    title: movie ? `${movie.title} - 电影在线观看与剧情介绍` : '影片未找到 - 人间中毒在线',
    description: movie
      ? `${movie.title}（${movie.year}）剧情简介：${movie.synopsis.slice(0, 60)}… 类型：${movie.genres.join('、')}，导演 ${movie.director}。`
      : '未找到对应的影片信息，返回首页浏览更多电影内容。',
    keywords: movie
      ? `人间中毒在线,${movie.title},${movie.genres.join(',')},剧情介绍,电影在线观看`
      : '人间中毒在线,电影',
  });

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(''), 2200);
  };

  const handleFavorite = () => {
    if (!movie) return;
    const next = toggleFavorite(movie.id);
    showToast(next ? '已加入收藏' : '已取消收藏');
  };

  const handleShare = async () => {
    if (!movie) return;
    const shareUrl = window.location.href;
    const shareData = { title: `${movie.title} - 人间中毒在线`, text: movie.tagline, url: shareUrl };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return;
      } catch {
        /* user cancelled */
        return;
      }
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      showToast('链接已复制到剪贴板');
    } catch {
      showToast('请手动复制浏览器地址分享');
    }
  };

  if (!movie) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="mx-auto flex w-full max-w-[1280px] flex-col items-center px-4 py-24 text-center md:px-8">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-emotion-sad-line text-3xl"></i>
            </span>
            <h1 className="mt-5 text-2xl font-bold text-foreground-950">没有找到这部影片</h1>
            <p className="mt-2 max-w-md text-sm text-foreground-600">
              该影片可能已下线或链接有误，返回首页看看其他精彩内容吧。
            </p>
            <Link
              to="/"
              className="mt-6 flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition hover:bg-accent-600 dark:text-foreground-950"
            >
              <i className="ri-home-4-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const related = getRelatedMovies(movie, 6);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-16">
        <div className="relative w-full overflow-hidden">
          <div className="relative h-[320px] w-full md:h-[420px]">
            <img
              src={movie.backdrop || movie.poster}
              alt={`${movie.title} 剧照`}
              title={`${movie.title} - 电影在线观看与剧情介绍`}
              className="h-full w-full object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/70 to-background-50/40"></div>
          </div>

          <div className="mx-auto mt-6 w-full max-w-[1280px] px-4 md:px-8">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '电影分类', to: '/category' },
                { label: movie.title },
              ]}
            />
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:gap-8">
              <div className="lg:col-span-4">
                <div className="relative w-full max-w-[280px] overflow-hidden rounded-lg border border-background-300 bg-background-100">
                  <div style={{ aspectRatio: '2 / 3' }}>
                    <img
                      src={movie.poster}
                      alt={`${movie.title} 电影海报`}
                      title={`${movie.title} - 电影在线观看与剧情介绍`}
                      className="h-full w-full object-top"
                    />
                  </div>
                  <span className="absolute left-3 top-3 flex items-center gap-1 rounded-full bg-secondary-500/95 px-2.5 py-1 text-sm font-bold text-background-50 dark:text-foreground-950">
                    <i className="ri-star-fill text-xs"></i>
                    {movie.rating.toFixed(1)}
                  </span>
                </div>

                <div className="mt-4 flex max-w-[280px] flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={handleFavorite}
                    className={`flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-semibold transition cursor-pointer ${
                      favorited
                        ? 'bg-accent-500 text-background-50 dark:text-foreground-950'
                        : 'border border-background-300 bg-background-100 text-foreground-900 hover:border-accent-400 hover:text-accent-700'
                    }`}
                  >
                    <i className={favorited ? 'ri-heart-3-fill' : 'ri-heart-3-line'}></i>
                    {favorited ? '已收藏' : '收藏'}
                  </button>
                  <button
                    type="button"
                    onClick={handleShare}
                    className="flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-100 px-4 py-2.5 text-sm font-medium text-foreground-900 transition hover:border-accent-400 hover:text-accent-700 cursor-pointer"
                  >
                    <i className="ri-share-forward-line"></i>
                    分享
                  </button>
                </div>
              </div>

              <div className="lg:col-span-8">
                <span className="inline-flex items-center gap-2 rounded-full border border-accent-400/50 bg-accent-500/15 px-3 py-1 text-xs font-medium text-accent-700">
                  {movie.status}
                </span>
                <h1 className="mt-3 text-3xl font-extrabold text-foreground-950 md:text-4xl">{movie.title}</h1>
                <p className="mt-3 text-sm italic text-foreground-600 md:text-base">「{movie.tagline}」</p>

                <div className="mt-5 flex flex-wrap gap-2">
                  {movie.genres.map((genre) => (
                    <Link
                      key={genre}
                      to={`/category?genre=${encodeURIComponent(genre)}`}
                      className="rounded-full border border-secondary-200 bg-secondary-100/25 px-3 py-1 text-xs text-secondary-900 transition hover:border-accent-400 hover:text-accent-700"
                    >
                      {genre}
                    </Link>
                  ))}
                </div>

                <dl className="mt-6 grid grid-cols-1 gap-x-8 gap-y-3 rounded-lg border border-background-200 bg-background-100 p-5 sm:grid-cols-2">
                  <div className="flex gap-3">
                    <dt className="w-16 shrink-0 text-sm text-foreground-500">上映时间</dt>
                    <dd className="text-sm text-foreground-900">{movie.year} 年</dd>
                  </div>
                  <div className="flex gap-3">
                    <dt className="w-16 shrink-0 text-sm text-foreground-500">地区</dt>
                    <dd className="text-sm text-foreground-900">{movie.region}</dd>
                  </div>
                  <div className="flex gap-3">
                    <dt className="w-16 shrink-0 text-sm text-foreground-500">导演</dt>
                    <dd className="text-sm text-foreground-900">{movie.director}</dd>
                  </div>
                  <div className="flex gap-3">
                    <dt className="w-16 shrink-0 text-sm text-foreground-500">时长</dt>
                    <dd className="text-sm text-foreground-900">{formatDuration(movie.duration)}</dd>
                  </div>
                  <div className="flex gap-3 sm:col-span-2">
                    <dt className="w-16 shrink-0 text-sm text-foreground-500">主演</dt>
                    <dd className="text-sm text-foreground-900">{movie.actors.join(' / ')}</dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <section className="mx-auto mt-10 w-full max-w-[1280px] px-4 md:mt-14 md:px-8">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <SectionHeading icon="ri-file-text-line" title="剧情简介" />
              <p className="text-sm leading-8 text-foreground-700 md:text-base">{movie.synopsis}</p>
            </div>

            <div className="lg:col-span-4">
              <SectionHeading icon="ri-play-circle-line" title="视频播放区" />
              <div className="relative w-full overflow-hidden rounded-lg border border-background-300 bg-background-950" style={{ aspectRatio: '16 / 9' }}>
                <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full border border-background-400 text-foreground-800">
                    <i className="ri-play-fill text-2xl"></i>
                  </span>
                  <p className="mt-4 text-sm font-semibold text-foreground-900">播放功能待接入</p>
                  <p className="mt-1 max-w-xs text-xs leading-relaxed text-foreground-500">
                    演示区域暂未接入真实视频流，感谢理解。上线后将支持在线观看体验。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto mt-12 w-full max-w-[1280px] px-4 md:mt-16 md:px-8">
          <SectionHeading
            icon="ri-movie-line"
            title="相关推荐"
            subtitle="根据类型与导演为你挑选的同类影片"
          />
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {related.map((item, i) => (
              <div key={item.id} className={`anim-fade-up delay-${(i % 5) + 1}`}>
                <MovieCard movie={item} />
              </div>
            ))}
          </div>
        </section>
      </main>

      <Footer />

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-[60] -translate-x-1/2 rounded-full border border-background-300 bg-background-900 px-5 py-2.5 text-sm font-medium text-foreground-50 anim-fade-up">
          {toast}
        </div>
      )}
    </div>
  );
}